import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Calendar, CheckCircle, ChevronRight, Clock, FileText, XCircle } from 'lucide-react';

import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import { getServiceNames, getTotalDuration, getTotalPrice } from '../utils/appointmentHelpers';
import { Appointment } from '../types';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';

const formatMoney = (n: number): string => {
  const v = Number.isFinite(n) ? n : 0;
  return v.toFixed(2);
};

const statusPill = (status: string) => {
  const s = String(status || '').toUpperCase();
  switch (s) {
    case 'CONFIRMED':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'PENDING':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'COMPLETED':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'CANCELLED':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const BookingSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { currentBusiness, isInitialized: businessInitialized } = useBusiness();

  const state = (location.state || {}) as any;
  const fromPath = typeof state?.from === 'string' ? state.from : '/dashboard/business?section=invoices';
  const queryEmail = useMemo(() => {
    try {
      return String(new URLSearchParams(location.search).get('email') || '').trim();
    } catch {
      return '';
    }
  }, [location.search]);
  const customerEmail = (queryEmail || String(state?.customerEmail || '')).trim();
  const customerName = String(state?.customerName || '').trim();
  const prefill = (state?.prefill && typeof state.prefill === 'object') ? state.prefill : null;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const [loading, setLoading] = useState(true);
  const [missingBusiness, setMissingBusiness] = useState(false);
  const [resolvedCustomerId, setResolvedCustomerId] = useState<string | null>(null);
  const [resolvedCustomerLabel, setResolvedCustomerLabel] = useState<string>('');
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const goBack = useCallback(() => {
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/dashboard/business?section=invoices');
  }, [fromPath, navigate]);

  const canLoad = useMemo(() => {
    if (!customerEmail) return false;
    if (!currentBusiness?.id) return false;
    return true;
  }, [customerEmail, currentBusiness?.id]);

  useEffect(() => {
    const load = async () => {
      if (!currentBusiness?.id) {
        if (!businessInitialized) return;
        setMissingBusiness(true);
        setLoading(false);
        return;
      }
      setMissingBusiness(false);

      if (!customerEmail) {
        // Don't toast on back/refresh: show inline message instead.
        setResolvedCustomerId(null);
        setResolvedCustomerLabel('');
        setAppointments([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await apiService.checkEmailExists(customerEmail.toLowerCase());
        if (!result?.exists || !result?.user?.id) {
          setResolvedCustomerId(null);
          setResolvedCustomerLabel('');
          setAppointments([]);
          return;
        }

        const cust = result.user;
        setResolvedCustomerId(cust.id);
        const label = `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || cust.email;
        setResolvedCustomerLabel(label);

        const resp = await apiService.getAppointments({
          businessId: currentBusiness.id,
          customerId: cust.id,
          limit: 50,
          page: 1,
          sort: 'desc',
        });
        setAppointments(resp.data || []);
      } catch (e) {
        console.error('Error loading bookings for customer:', e);
        toast.error('Failed to load bookings');
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentBusiness?.id, businessInitialized, customerEmail]);

  const handleSelectAppointment = (apt: any) => {
    if (!apt?.id) return;
    if (apt?.invoiceId) return;

    navigate(`/dashboard/business/appointments/${apt.id}/create-invoice`, {
      state: {
        from: `/dashboard/business/invoices/booking-selection?email=${encodeURIComponent(customerEmail)}`,
        ...(prefill ? { prefill } : {}),
      },
    });
  };

  if (missingBusiness) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
          <div className="text-lg font-semibold text-gray-900">No business selected</div>
          <div className="text-sm text-gray-600 mt-2">
            Please select a business (or ensure your business role is active) to continue.
          </div>
          <button onClick={goBack} className="mt-5 aacore-btn2-outline">
            Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />

      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="invoices"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
            unreadNotificationCount={0}
          />
        )}

        <div className="flex flex-col min-h-screen">
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Booking Selection</h1>
              <div className="w-10"></div>
            </div>
          </div>

          <div className="flex-1 p-4 md:p-8">
            <button
              onClick={goBack}
              className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="hidden lg:block text-2xl font-semibold text-gray-900">
                  Booking Selection
                </h1>
                <div className="text-sm text-gray-600 mt-1">
                  Select a booking for{' '}
                  <span className="font-semibold text-gray-900">
                    {resolvedCustomerLabel || customerName || customerEmail}
                  </span>
                  {' '}to generate an invoice with its services.
                </div>
              </div>
            </div>

            {!customerEmail ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">Customer email is missing</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Go back and enter a customer email to view bookings.
                    </div>
                  </div>
                </div>
              </div>
            ) : !resolvedCustomerId ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">Customer not found</div>
                    <div className="text-sm text-gray-600 mt-1">
                      We couldn’t find a customer with email <span className="font-semibold">{customerEmail}</span>.
                    </div>
                  </div>
                </div>
              </div>
            ) : appointments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">No bookings found</div>
                    <div className="text-sm text-gray-600 mt-1">
                      This customer has no bookings for the current business.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map((apt: any) => {
                  const scheduledAt = apt?.scheduledAt ? new Date(apt.scheduledAt) : null;
                  const dateLabel = scheduledAt && Number.isFinite(scheduledAt.getTime())
                    ? scheduledAt.toLocaleDateString()
                    : '—';
                  const timeLabel = scheduledAt && Number.isFinite(scheduledAt.getTime())
                    ? scheduledAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                    : '';
                  const servicesLabel = getServiceNames(apt as Appointment);
                  const duration = getTotalDuration(apt as Appointment);
                  const total = getTotalPrice(apt as Appointment);
                  const hasInvoice = !!apt?.invoiceId;

                  return (
                    <div
                      key={apt.id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${statusPill(apt?.status)}`}>
                            {String(apt?.status || '—').toUpperCase()}
                          </span>
                          {hasInvoice && (
                            <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border bg-gray-50 text-gray-700 border-gray-200">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Invoice already exists
                            </span>
                          )}
                        </div>

                        <div className="mt-2 text-sm font-semibold text-gray-900 truncate">
                          {servicesLabel}
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {dateLabel}{timeLabel ? ` • ${timeLabel}` : ''}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {duration} min
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" />
                            ${formatMoney(total)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2">
                        {hasInvoice ? (
                          <button
                            type="button"
                            className="aacore-btn2-secondary inline-flex items-center gap-2"
                            onClick={() => navigate(`/dashboard/business/invoices/${apt.invoiceId}`, { state: { from: '/dashboard/business/invoices/booking-selection' } })}
                          >
                            View invoice
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="aacore-btn2-primary inline-flex items-center gap-2"
                            onClick={() => handleSelectAppointment(apt)}
                          >
                            Select
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSelectionPage;

